// The Schedule of Work's areas: include or leave out, order, and per photo
// ★ (the cover photo) or leave it out of this document. Nothing here changes
// the areas themselves.
import { ArrowDown, ArrowUp, Star, X } from 'lucide-react';
import type { DevicePhoto, DeviceRecord } from '../lib/device-db';
import type { PhotoRef, ScheduleOptions } from '../lib/schedule/model';

interface Props {
  areas: DeviceRecord[];
  photos: PhotoRef[];
  /** Device photos, for their thumbnails. */
  onDevice: DevicePhoto[];
  options: ScheduleOptions;
  onChange: (change: Partial<ScheduleOptions>) => void;
}

const ARROW = 'flex h-10 w-10 items-center justify-center text-slate-500 disabled:cursor-default disabled:text-slate-300';

function Thumb({ photo, device }: { photo: PhotoRef; device: DevicePhoto | undefined }) {
  const src = device ? (typeof device.thumbnail === 'string' ? device.thumbnail : URL.createObjectURL(device.thumbnail)) : null;
  return src ? <img src={src} alt={photo.name} className="h-16 w-16 rounded object-cover" /> : <span className="flex h-16 w-16 items-center justify-center rounded bg-slate-100 p-1 text-center text-[10px] text-slate-500">{photo.name.replace(/\.jpe?g$/i, '')}</span>;
}

export function ScheduleAreas({ areas, photos, onDevice, options, onChange }: Props) {
  const deviceById = new Map(onDevice.map((p) => [p.id, p]));
  const included = new Set(options.areaIds);
  const ordered = [...options.areaIds.map((id) => areas.find((a) => a.id === id)).filter((a): a is DeviceRecord => !!a), ...areas.filter((a) => !included.has(a.id))];
  const left = new Set(options.leftOutPhotoIds);

  const toggle = (id: string) => onChange({ areaIds: included.has(id) ? options.areaIds.filter((x) => x !== id) : [...options.areaIds, id] });
  const move = (id: string, by: -1 | 1) => {
    const ids = [...options.areaIds];
    const i = ids.indexOf(id);
    const j = i + by;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    onChange({ areaIds: ids });
  };
  const leaveOut = (id: string) => onChange({ leftOutPhotoIds: left.has(id) ? options.leftOutPhotoIds.filter((x) => x !== id) : [...options.leftOutPhotoIds, id], ...(options.coverPhotoId === id ? { coverPhotoId: null } : {}) });

  return (
    <section className="space-y-2 rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="font-medium text-slate-900">
        Areas <span className="text-sm font-normal text-slate-500">({options.areaIds.length} of {areas.length} included; internal areas print first)</span>
      </h2>
      {ordered.map((area) => {
        const on = included.has(area.id);
        const areaPhotos = photos.filter((p) => p.areaId === area.id);
        // An arrow that can't move the area is shown greyed out, not silently ignored (#396).
        const place = options.areaIds.indexOf(area.id);
        const isFirst = place === 0;
        const isLast = place === options.areaIds.length - 1;
        return (
          <div key={area.id} className={`rounded-lg border p-3 ${on ? 'border-slate-200' : 'border-dashed border-slate-200 opacity-60'}`}>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={on} onChange={() => toggle(area.id)} aria-label={`Include ${String(area.fields.hig_name ?? '')}`} className="h-5 w-5" />
              <span className="flex-1 text-sm font-medium text-slate-900">
                {String(area.fields.hig_name ?? '')} <span className="text-xs font-normal text-slate-500">{String(area.fields.hig_side ?? '')}</span>
              </span>
              {on && (
                <>
                  <button type="button" onClick={() => move(area.id, -1)} disabled={isFirst} aria-label="Move up" className={ARROW}>
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={() => move(area.id, 1)} disabled={isLast} aria-label="Move down" className={ARROW}>
                    <ArrowDown className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
            {on && options.includePhotos && areaPhotos.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {areaPhotos.map((p) => (
                  <div key={p.id} className={`relative ${left.has(p.id) ? 'opacity-30' : ''}`}>
                    <Thumb photo={p} device={deviceById.get(p.id)} />
                    <button type="button" onClick={() => onChange({ coverPhotoId: options.coverPhotoId === p.id ? null : p.id })} aria-label={`Cover photo: ${p.name}`} className="absolute left-0 top-0 rounded-br bg-white/80 p-0.5">
                      <Star className={`h-4 w-4 ${options.coverPhotoId === p.id ? 'fill-primary text-primary' : 'text-slate-500'}`} />
                    </button>
                    <button type="button" onClick={() => leaveOut(p.id)} aria-label={`${left.has(p.id) ? 'Put back' : 'Leave out'}: ${p.name}`} className="absolute right-0 top-0 rounded-bl bg-white/80 p-0.5">
                      <X className="h-4 w-4 text-slate-600" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <p className="text-xs text-slate-500">★ the cover photo for this document (it starts as the project&rsquo;s, set on the iPad) · × leaves a photo out of this document.</p>
    </section>
  );
}
